import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Copy, Download, ExternalLink, QrCode, Share2, X } from 'lucide-react';
import { useMarkBioSharedMutation, useMeQuery } from '../profiles/api';
import { useLastingLinkQuery, useResetLastingLinkMutation } from '../inviteLinks/api';
import { useDialogTrap } from '../letters/useDialogTrap';
import { qrMatrix, qrPath } from './qr';
import { BIO_LINES, toiypAddress, withVia } from './share';
import { drawStoryCard, storyCardFile } from './storyCard';
import { track } from './track';

/**
 * The share kit (onboarding plan, slice 3): "Put your toiyp everywhere".
 * The address in one tap, bio lines for each app, a story card drawn in the
 * browser, and "Show my toiyp" — a full-screen QR for meeting people. Shown
 * as a tour step, and in a sheet from the profile, the ⋯ menu and Getting
 * started.
 */

export type ShareKitFrom = 'tour' | 'profile' | 'menu' | 'getting_started' | 'home_panel' | 'delivered_prompt';

export async function copy(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Some browsers (and embedded webviews) refuse the async clipboard; the
    // old selection copy still works inside the same tap.
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    try {
      return document.execCommand('copy');
    } catch {
      return false;
    } finally {
      area.remove();
    }
  }
}

/** A copy button that says "Copied" for a moment. */
export function CopyButton({
  text,
  label,
  onCopied,
  className = 'share-kit__copy',
}: {
  text: string;
  label: string;
  onCopied?: () => void;
  className?: string;
}) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => setDone(false), 1800);
    return () => clearTimeout(t);
  }, [done]);
  return (
    <button
      type="button"
      className={className}
      aria-label={label}
      onClick={() =>
        void copy(text).then((ok) => {
          if (!ok) return;
          setDone(true);
          onCopied?.();
        })
      }
    >
      {done ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
      {done ? 'Copied' : 'Copy'}
    </button>
  );
}

export function ShareKit({ from }: { from: ShareKitFrom }) {
  const { data: me } = useMeQuery();
  // The lasting link: a QR, a story or a bio is printed or posted, and should keep working.
  const { data: link } = useLastingLinkQuery();
  const [resetLink, { isLoading: resetting }] = useResetLastingLinkMutation();
  const [confirmReset, setConfirmReset] = useState(false);
  const [markBioShared] = useMarkBioSharedMutation();
  const [showQr, setShowQr] = useState(false);
  const [storyMsg, setStoryMsg] = useState<string | null>(null);
  const [sharingStory, setSharingStory] = useState(false);
  const preview = useRef<HTMLCanvasElement>(null);
  const handle = me?.handle ?? null;

  useEffect(() => {
    track({ event: 'share_kit_viewed', from });
  }, [from]);

  const story = useMemo(
    () =>
      me?.handle && link
        ? { displayName: me.displayName, handle: me.handle, avatar: me.avatar, qrUrl: withVia(link.url, 'story') }
        : null,
    [me?.displayName, me?.handle, me?.avatar, link],
  );

  // The preview is the real card, drawn small.
  useEffect(() => {
    if (story && preview.current) void drawStoryCard(preview.current, story);
  }, [story]);

  const onShareStory = useCallback(async () => {
    if (!story) return;
    setStoryMsg(null);
    setSharingStory(true);
    try {
      const file = await storyCardFile(story);
      track({ event: 'share_opened', channel: 'story' });
      if (navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: 'My toiyp' });
          void markBioShared();
          return;
        } catch (e) {
          if ((e as { name?: string }).name === 'AbortError') return;
        }
      }
      // No file sharing here (most desktops): save it instead.
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      void markBioShared();
      setStoryMsg('Saved to your downloads. Add it to your story from there.');
    } catch {
      setStoryMsg('Could not draw your card just now.');
    } finally {
      setSharingStory(false);
    }
  }, [story, markBioShared]);

  if (!handle) return null;
  const address = toiypAddress(handle);

  return (
    <div className="share-kit">
      <section className="share-kit__address" aria-label="Your toiyp">
        <span className="share-kit__address-text toiyp-address">{address}</span>
        <CopyButton text={address} label="Copy your toiyp address" />
      </section>

      <button type="button" className="share-kit__show" onClick={() => setShowQr(true)}>
        <QrCode size={20} aria-hidden="true" />
        <span>
          <span className="share-kit__show-title">Show my toiyp</span>
          <span className="share-kit__show-note">A code to scan when you meet someone</span>
        </span>
      </button>

      <section className="share-kit__section" aria-labelledby="share-kit-story">
        <h3 className="share-kit__h" id="share-kit-story">
          A card for your story
        </h3>
        <div className="share-kit__story">
          <canvas ref={preview} className="share-kit__story-preview" aria-label="Your story card" role="img" />
          <div className="share-kit__story-side">
            <p className="share-kit__note">
              Pop it on your Instagram or WhatsApp story. The code takes people straight to a blank
              page addressed to you.
            </p>
            <button
              type="button"
              className="btn btn--primary"
              disabled={!story || sharingStory}
              onClick={() => void onShareStory()}
            >
              {typeof navigator.canShare === 'function' ? (
                <Share2 size={15} aria-hidden="true" />
              ) : (
                <Download size={15} aria-hidden="true" />
              )}
              {sharingStory ? 'Drawing…' : 'Share to your story'}
            </button>
            {storyMsg && (
              <p className="share-kit__note" role="status">
                {storyMsg}
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="share-kit__section" aria-labelledby="share-kit-bio">
        <h3 className="share-kit__h" id="share-kit-bio">
          A line for your bio
        </h3>
        <ul className="share-kit__bios">
          {BIO_LINES.map(({ platform, label, line, edit }) => (
            <li key={platform} className="share-kit__bio">
              <span className="share-kit__bio-label">{label}</span>
              <span className="share-kit__bio-line">{line(handle)}</span>
              <span className="share-kit__bio-actions">
                <CopyButton
                  text={line(handle)}
                  label={`Copy the ${label} line`}
                  onCopied={() => {
                    track({ event: 'bio_line_copied', platform });
                    void markBioShared();
                  }}
                />
                {edit && (
                  <a className="share-kit__edit" href={edit.url} target="_blank" rel="noopener noreferrer">
                    {edit.label} <ExternalLink size={12} aria-hidden="true" />
                  </a>
                )}
              </span>
            </li>
          ))}
          {link && (
            <li className="share-kit__bio">
              <span className="share-kit__bio-label">The link in your bio</span>
              <span className="share-kit__bio-line">{withVia(link.url, 'bio')}</span>
              <span className="share-kit__bio-actions">
                <CopyButton
                  text={withVia(link.url, 'bio')}
                  label="Copy your invite link"
                  onCopied={() => {
                    track({ event: 'share_opened', channel: 'bio' });
                    void markBioShared();
                  }}
                />
              </span>
            </li>
          )}
        </ul>
      </section>

      {link && (
        <section className="share-kit__section share-kit__lasting" aria-labelledby="share-kit-lasting">
          <h3 className="share-kit__h" id="share-kit-lasting">
            Your lasting link
          </h3>
          <p className="share-kit__note">
            Your code, story card and bio all use one link that doesn&apos;t expire. It welcomes up to{' '}
            {link.dailyCap} new people a day, so a post that travels can&apos;t flood your letterbox.
          </p>
          {confirmReset ? (
            <div className="share-kit__reset-confirm" role="group" aria-label="Reset your link">
              <p className="share-kit__note">
                Your old code, story card and bio link will stop working. Anyone already writing to you
                is unaffected.
              </p>
              <span className="share-kit__bio-actions">
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={resetting}
                  onClick={() => void resetLink().then(() => setConfirmReset(false))}
                >
                  {resetting ? 'Resetting…' : 'Reset my link'}
                </button>
                <button type="button" className="linkish" onClick={() => setConfirmReset(false)}>
                  Keep it
                </button>
              </span>
            </div>
          ) : (
            <button type="button" className="linkish share-kit__reset" onClick={() => setConfirmReset(true)}>
              Reset link
            </button>
          )}
        </section>
      )}

      {showQr && <ShowMyToiyp onClose={() => setShowQr(false)} />}
    </div>
  );
}

/**
 * "Show my toiyp": a full-screen QR for meeting someone face to face. It
 * carries the lasting link, so the same code keeps working until the member
 * resets it.
 */
export function ShowMyToiyp({ onClose }: { onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const { data: me } = useMeQuery();
  const { data: link, isError } = useLastingLinkQuery(undefined, { refetchOnMountOrArgChange: true });
  useDialogTrap(ref, onClose);

  useEffect(() => {
    track({ event: 'share_opened', channel: 'qr' });
    // Keep the screen on while someone scans; harmless where unsupported.
    let lock: { release: () => Promise<void> } | null = null;
    void (navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<typeof lock> } }).wakeLock
      ?.request('screen')
      .then((l) => (lock = l))
      .catch(() => {});
    return () => void lock?.release().catch(() => {});
  }, []);

  const url = link ? withVia(link.url, 'qr') : null;
  const matrix = useMemo(() => (url ? qrMatrix(url) : null), [url]);

  return (
    <div className="show-toiyp" role="dialog" aria-modal="true" aria-label="Show my toiyp" tabIndex={-1} ref={ref}>
      <button type="button" className="show-toiyp__close icon-btn" onClick={onClose} aria-label="Close">
        <X size={18} />
      </button>
      <p className="show-toiyp__brand brand-wordmark">toiyp</p>
      <div className="show-toiyp__code">
        {matrix ? (
          <svg
            viewBox={`-2 -2 ${matrix.length + 4} ${matrix.length + 4}`}
            role="img"
            aria-label="A QR code that opens a blank letter to you"
            shapeRendering="crispEdges"
          >
            <rect x="-2" y="-2" width={matrix.length + 4} height={matrix.length + 4} fill="#fff" />
            <path d={qrPath(matrix)} fill="#2C2420" />
          </svg>
        ) : (
          <p className="share-kit__note">{isError ? 'Could not fetch your code just now.' : 'Fetching your code…'}</p>
        )}
      </div>
      <p className="show-toiyp__line">Met someone? Let them scan this instead of taking your number.</p>
      {me?.handle && <p className="show-toiyp__address toiyp-address">{toiypAddress(me.handle)}</p>}
      {link && link.leftToday === 0 && (
        <p className="show-toiyp__quiet" role="status">
          Your letterbox has welcomed all the new people it can today. People already writing to
          you can still scan it.
        </p>
      )}
    </div>
  );
}

/** The share kit in a sheet — from the profile, the ⋯ menu and Getting started. */
export function ShareKitSheet({ from, onClose }: { from: Exclude<ShareKitFrom, 'tour'>; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useDialogTrap(ref, onClose);
  return (
    <div className="how-dialog__backdrop" onClick={onClose}>
      <div
        className="how-dialog share-kit-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-kit-title"
        tabIndex={-1}
        ref={ref}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="how-dialog__close icon-btn" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <h2 className="first-run__title" id="share-kit-title">
          Put your toiyp everywhere
        </h2>
        <p className="first-run__lead">Wherever people find you, let them find your letterbox too.</p>
        <ShareKit from={from} />
      </div>
    </div>
  );
}
